import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../lib/api";
import { useI18n } from "../context/I18nContext";

/**
 * Public questionnaire — accessed via tokenised link sent by the admin
 * to a qualified lead. No login required.
 */
export default function QuestionnaireFill() {
  const { token } = useParams();
  const { t, lang, tr } = useI18n();
  const [data, setData] = useState(null);
  const [answers, setAnswers] = useState({});
  const [status, setStatus] = useState("idle"); // idle | sending | done | err
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get(`/quotes/by-token/${token}`).then((r) => {
      setData(r.data);
      if (r.data.questionnaire_answers) setAnswers(r.data.questionnaire_answers);
    }).catch((e) => setErr(e?.response?.data?.detail || "Lien invalide"));
  }, [token]);

  const setAnswer = (id, v) => setAnswers((a) => ({ ...a, [id]: v }));
  const toggle = (id, opt) => setAnswers((a) => {
    const cur = Array.isArray(a[id]) ? a[id] : [];
    return { ...a, [id]: cur.includes(opt) ? cur.filter((x) => x !== opt) : [...cur, opt] };
  });

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      await api.post(`/quotes/by-token/${token}/answers`, { answers });
      setStatus("done");
    } catch (e2) {
      setStatus("err");
      setErr(e2?.response?.data?.detail || "Erreur");
    }
  };

  if (err && !data) {
    return (
      <div className="max-w-xl mx-auto px-6 py-24 text-center" data-testid="questionnaire-invalid">
        <div className="overline mb-3 text-red-400">Lien invalide</div>
        <h1 className="serif text-3xl">Ce lien n'est plus actif.</h1>
        <p className="mt-4 text-[var(--gai-grey-700)]">{String(err)}</p>
      </div>
    );
  }

  if (!data) return <div className="p-24 text-center">{t("common.loading")}</div>;

  if (data.questionnaire_completed_at && status !== "done") {
    return (
      <div className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="questionnaire-already-done">
        <div className="overline mb-3 text-[var(--gai-red)]">Déjà complété</div>
        <h1 className="serif text-4xl">Merci, {data.full_name}.</h1>
        <p className="mt-4 text-[var(--gai-grey-700)]">
          Vos réponses ont été transmises à notre service commercial. Nous reviendrons vers vous avec une proposition sur mesure.
        </p>
      </div>
    );
  }

  if (status === "done") {
    return (
      <div className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="questionnaire-success">
        <div className="overline mb-3 text-[var(--gai-red)]">Reçu</div>
        <h1 className="serif text-5xl">Merci.</h1>
        <p className="mt-6 text-lg text-[var(--gai-grey-700)]">
          Notre service commercial finalise votre proposition. Vous recevrez le devis détaillé sous 48 à 72h.
        </p>
      </div>
    );
  }

  const Q = data.questionnaire;

  return (
    <div className="max-w-3xl mx-auto px-6 lg:px-10 py-16" data-testid="page-questionnaire">
      <div className="overline mb-3">Étape 2/3 · Qualification</div>
      <h1 className="serif text-4xl md:text-5xl leading-[1.05]">{tr(Q.title)}</h1>
      <p className="mt-4 text-[var(--gai-grey-700)]">{tr(Q.intro)}</p>
      <div className="mt-6 p-4 border border-[var(--gai-grey-300)] text-sm">
        <span className="overline mr-3">Référence</span>
        <span className="text-[var(--gai-grey-800)]">{data.full_name} · {data.email}{data.company ? ` · ${data.company}` : ""}</span>
      </div>

      <form onSubmit={submit} className="mt-10 space-y-10" data-testid="questionnaire-form">
        {Q.questions.map((q) => (
          <div key={q.id} data-testid={`q-${q.id}`}>
            <label className="gai-label">{tr(q.label)}</label>
            {q.type === "text" && (
              <input
                className="gai-input"
                value={answers[q.id] || ""}
                onChange={(e) => setAnswer(q.id, e.target.value)}
              />
            )}
            {q.type === "textarea" && (
              <textarea
                rows={4}
                className="gai-input resize-none"
                value={answers[q.id] || ""}
                onChange={(e) => setAnswer(q.id, e.target.value)}
              />
            )}
            {q.type === "select" && (
              <select
                className="gai-input appearance-none"
                value={answers[q.id] || ""}
                onChange={(e) => setAnswer(q.id, e.target.value)}
              >
                <option value="" style={{ background: "var(--gai-white)", color: "var(--gai-black)" }}>—</option>
                {q.options.map((o) => (
                  <option key={o} value={o} style={{ background: "var(--gai-white)", color: "var(--gai-black)" }}>{o}</option>
                ))}
              </select>
            )}
            {q.type === "checkboxes" && (
              <div className="flex flex-wrap gap-2 mt-2">
                {q.options.map((o) => {
                  const active = (answers[q.id] || []).includes(o);
                  return (
                    <button
                      type="button"
                      key={o}
                      onClick={() => toggle(q.id, o)}
                      className={`px-3 py-2 text-[0.72rem] tracking-[0.16em] uppercase border ${
                        active
                          ? "bg-[var(--gai-red)] text-[var(--gai-white)] border-[var(--gai-red)]"
                          : "border-[var(--gai-grey-200)] hover:border-[var(--gai-red)]"
                      }`}
                      data-testid={`q-${q.id}-${o.replace(/\s+/g, '-').toLowerCase()}`}
                    >
                      {o}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        ))}

        <div className="flex items-center gap-4 pt-4 border-t border-[var(--gai-grey-200)]">
          <button disabled={status === "sending"} className="gai-btn-gold" data-testid="questionnaire-submit">
            {status === "sending" ? t("common.sending") : "Transmettre mes réponses"}
          </button>
          {status === "err" && <span className="text-red-300 text-sm" data-testid="questionnaire-err">{String(err)}</span>}
        </div>
      </form>
    </div>
  );
}
